"use client";

import ColourComponent from "@JohnLewisPartnership/jl-design-system/dist/components/colour/Colour";
import ColourList from "@JohnLewisPartnership/jl-design-system/dist/components/colour/ColourList";
import Link from "next/link";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";
import classNames from "classnames/bind";
import styles from "./Colours.module.scss";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";
import { ColourOption } from "@/components/Colours/types.ts";
import { ColourList as SimplifiedColourList } from "./ColourList/ColourList";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";
import {
  SWATCHES_SELECT_COLOUR,
  SWATCHES_SELECT_PROMO_COLOUR,
  SWATCHES_SELECT_POSITION_11_PLUS,
  SWATCHES_SELECT_POSITION_6_10,
  SWATCHES_SELECT_POSITION_1_5,
} from "@/components/Colours/ColourList/monetateEvents.ts";

const cx = classNames.bind(styles);

type Props = {
  /**
   * A number representing the rows to display.
   */
  rowsToDisplay: number;
  /**
   * An array of colour option objects.
   */
  colourOptions: ColourOption[];
  /**
   * A string which represents the product brand name.
   */
  brand?: string;
  /**
   * A boolean toggling from regular and simplified version of colour selector
   */
  useSimplifiedVersion: boolean;
  /**
   * A boolean which indicates if the device is mobile.
   */
  isMobile: boolean;
  /**
   * A boolean toggling history push with replace
   */
  replaceHistoryEntryOnColourChange: boolean;
};

export const buildColourSwatchUrl = (
  colourSwatchUrl: string | null | undefined,
) => {
  const { baseUrl, query } = getBaseUrlAndQuery(colourSwatchUrl) || [];
  if (baseUrl) {
    const colourSwatchParams = new URLSearchParams(query);
    colourSwatchParams.append("wid", "24");
    colourSwatchParams.append("hei", "24");
    colourSwatchParams.append("sm", "c");
    return `${baseUrl}?${colourSwatchParams.toString()}`;
  }
  return;
};

export const ColoursUI = ({
  rowsToDisplay,
  colourOptions,
  brand,
  useSimplifiedVersion,
  isMobile,
  replaceHistoryEntryOnColourChange,
}: Props) => {
  const selectedColourOption = colourOptions.find((c) => c.selected);

  const handleSwatchClick = (colourName: string) => {
    const colourOptionIndex = colourOptions.findIndex(
      (colour) => colour.colour === colourName,
    );
    sendEvent(SWATCHES_SELECT_COLOUR);

    if (colourOptions[colourOptionIndex]?.showReduced) {
      sendEvent(SWATCHES_SELECT_PROMO_COLOUR);
    }

    if (colourOptionIndex >= 0 && colourOptionIndex <= 4) {
      sendEvent(SWATCHES_SELECT_POSITION_1_5);
    } else if (colourOptionIndex >= 5 && colourOptionIndex <= 9) {
      sendEvent(SWATCHES_SELECT_POSITION_6_10);
    } else {
      sendEvent(SWATCHES_SELECT_POSITION_11_PLUS);
    }
  };

  if (useSimplifiedVersion) {
    return (
      <SimplifiedColourList
        colourOptions={colourOptions}
        brand={brand}
        isMobile={isMobile}
        replaceHistoryEntryOnColourChange={replaceHistoryEntryOnColourChange}
      />
    );
  }

  return (
    <ColourList
      showMore={rowsToDisplay}
      data-testid="colour:option"
      selectedLabel={selectedColourOption?.colour}
      containerProps={{ ["data-component"]: "Colours" }}
      containerClassName={cx("colourContainer")}
      brand={brand}
      hasDiorPromosEnabled={true}
      onClick={handleSwatchClick}
    >
      {colourOptions.map((colourOption, index) => {
        return (
          <ColourComponent
            key={`colourOption-${index}`}
            Tag={Link}
            buttonProps={{
              href: colourOption.url,
              shallow: true,
              scroll: false,
              replace: replaceHistoryEntryOnColourChange,
            }}
            selected={colourOption.colour === selectedColourOption?.colour}
            unavailable={!colourOption.showAsAvailable}
            name="colourSelector"
            value={colourOption.colour}
            swatch={buildColourSwatchUrl(colourOption.colourSwatch)}
            hasReducedPrice={colourOption.showReduced}
            reducedPriceText={colourOption.reducedLabel}
            isDior={isDiorBrand(brand)}
          />
        );
      })}
    </ColourList>
  );
};
